import * as React from 'react';
import type { Modifier, Modifiers } from 'react-day-picker';
import ReactDOM from 'react-dom';

import { singleDatePickerModifiers } from './modifiers';
import { SingleDatePickerNavbar } from './SingleDatePickerNavbar';
import { ClickAwayListener } from '@material-ui/core';
import moment, { type Moment } from 'moment';

import { BasePicker } from '../BasePicker';
import type { DatePickerAlignment } from '../util/alignment';
import { pickerPosition, singleDatePicker } from '../util/styles';

export interface SingleDatePickerBodyProps {
  selectedDate?: Moment;

  hoveredDate?: Moment;
  inputValue: string;
  inputError: boolean;
  isDayPickerOpen: boolean;
  outsideDivRef?: HTMLDivElement;
  disabledDays?: Modifier | Modifier[];
  modifiers?: Partial<Modifiers>;
  fromMonth?: Date;
  toMonth?: Date;
  calendarFootnotes?: React.ReactNode;
  portal?: boolean;
  error?: boolean;
  errorMessage?: string;
  handleDayChange: (day: Moment) => void;
  handleDayMouseEnter?: (day: Moment) => void;
  handleDayMouseLeave?: () => void;
  handleInputChange: (inputValue: string) => void;
  setInnerInput: (innerInput: HTMLElement | undefined) => void;
  onClickAway: () => void;
  hideCalendarClass?: string;
  alignment?: DatePickerAlignment;
  readOnly?: boolean;
}

export class SingleDatePickerBody extends React.Component<SingleDatePickerBodyProps> {
  private el: HTMLDivElement | undefined;

  public componentDidMount() {
    if (this.props.portal) {
      this.el = document.createElement('div');
      document.body.appendChild(this.el);
    }
  }

  public componentWillUnmount() {
    if (this.props.portal && this.el) {
      document.body.removeChild(this.el);
    }
  }

  private handleDayMouseDown = (dayDate: Date) => {
    const day = moment(dayDate).hour(0);
    this.props.handleDayChange(day);
  };

  private handleDayMouseEnter = (dayDate: Date) => {
    const day = moment(dayDate).hour(0);

    if (this.props.handleDayMouseEnter) {
      this.props.handleDayMouseEnter(day);
    }
  };

  private handleDayMouseLeave = () => {
    if (this.props.handleDayMouseLeave) {
      this.props.handleDayMouseLeave();
    }
  };

  private handleInputChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const { value } = event.target;
    this.props.handleInputChange(value);
  };

  private singleDatePickerBody = (portalStyles?: string) => (
    <ClickAwayListener
      onClickAway={this.props.onClickAway}
      mouseEvent={this.props.isDayPickerOpen ? 'onMouseUp' : false}
    >
      <div
        className={`${singleDatePicker.wrapper(this.props.isDayPickerOpen)} ${
          portalStyles || ''
        } ${!this.props.isDayPickerOpen ? this.props.hideCalendarClass : ''} `}
      >
        <SingleDatePickerNavbar
          inputError={this.props.inputError}
          inputValue={this.props.inputValue}
          inputRef={this.props.setInnerInput}
          handleInputChange={this.handleInputChange}
          error={this.props.error}
          errorMessage={this.props.errorMessage}
          readOnly={this.props.readOnly}
        />
        <BasePicker
          canChangeMonth
          numberOfMonths={1}
          onDayMouseDown={this.handleDayMouseDown}
          onDayMouseEnter={this.handleDayMouseEnter}
          onDayMouseLeave={this.handleDayMouseLeave}
          selectedDays={singleDatePickerModifiers.defineSelectedDays(
            this.props
          )}
          modifiers={singleDatePickerModifiers.defineModifiers(this.props)}
          modifiersStyles={singleDatePicker.modifierStyles}
          classNames={singleDatePicker.classNames}
          month={this.props.selectedDate?.toDate()}
          disabledDays={this.props.disabledDays}
          fromMonth={this.props.fromMonth}
          toMonth={this.props.toMonth}
        />
        {this.props.calendarFootnotes}
      </div>
    </ClickAwayListener>
  );

  public render() {
    if (this.props.portal && this.props.outsideDivRef && this.el) {
      const { top, left } = this.props.outsideDivRef.getBoundingClientRect();
      const rightPosition = this.props.outsideDivRef.offsetLeft;
      const portalStyles = pickerPosition(
        top,
        rightPosition,
        left,
        this.props.alignment
      );
      return ReactDOM.createPortal(
        this.singleDatePickerBody(portalStyles),
        this.el
      );
    }

    return this.singleDatePickerBody();
  }
}
